import Phaser from 'phaser';
import { Bullet } from '../entities/Bullet';

const BULLET_SPEED = 400;
const BULLET_MAX_RANGE = 300;
const COLLISION_RADIUS = 12;
// Minimum distance a bullet must travel before it can hit anything.
// Prevents bullets from immediately colliding with the soldier that fired them.
const BULLET_ARMING_DISTANCE = 20;

export interface Damageable {
  x: number;
  y: number;
  takeDamage(dmg: number): void;
  isDead(): boolean;
}

export class CombatSystem {
  private playerBullets: Bullet[] = [];
  private enemyBullets: Bullet[] = [];
  private scene: Phaser.Scene;
  private mapWidth: number;
  private mapHeight: number;
  private isWall: ((x: number, y: number) => boolean) | null = null;

  constructor(scene: Phaser.Scene, mapWidth: number, mapHeight: number) {
    this.scene = scene;
    this.mapWidth = mapWidth;
    this.mapHeight = mapHeight;
  }

  /**
   * Provide a callback that returns true when a world position is a non-walkable wall tile.
   * When set, bullets are destroyed on contact with walls.
   */
  setWallChecker(fn: (x: number, y: number) => boolean): void {
    this.isWall = fn;
  }

  /**
   * Fire a bullet.
   * @param fromEnemy - if true, bullet is an enemy bullet (orange tint)
   */
  fireBullet(
    fromX: number,
    fromY: number,
    toX: number,
    toY: number,
    fromEnemy: boolean = false
  ): Bullet {
    const bullet = new Bullet(this.scene, fromX, fromY);
    bullet.maxRange = BULLET_MAX_RANGE;

    if (fromEnemy) {
      bullet.setFillStyle(0xff8800); // orange for enemy bullets
    }

    const angle = Phaser.Math.Angle.Between(fromX, fromY, toX, toY);
    bullet.velocityX = Math.cos(angle) * BULLET_SPEED;
    bullet.velocityY = Math.sin(angle) * BULLET_SPEED;

    if (fromEnemy) {
      this.enemyBullets.push(bullet);
    } else {
      this.playerBullets.push(bullet);
    }

    // Emit muzzle flash event so VFX layer can react
    this.scene.events.emit('muzzleFlash', fromX, fromY);

    return bullet;
  }

  update(delta: number): void {
    this.updateBulletList(this.playerBullets, delta);
    this.updateBulletList(this.enemyBullets, delta);
  }

  private updateBulletList(bullets: Bullet[], delta: number): void {
    for (let i = bullets.length - 1; i >= 0; i--) {
      const bullet = bullets[i];
      if (!bullet.active) {
        bullets.splice(i, 1);
        continue;
      }
      bullet.update(delta);

      const offMap =
        bullet.x < 0 ||
        bullet.y < 0 ||
        bullet.x > this.mapWidth ||
        bullet.y > this.mapHeight;

      if (bullet.hasExceededRange() || offMap) {
        bullet.destroy();
        bullets.splice(i, 1);
        continue;
      }

      if (this.isWall?.(bullet.x, bullet.y)) {
        bullet.destroy();
        bullets.splice(i, 1);
      }
    }
  }

  /** Check player bullets against enemy targets */
  checkPlayerBulletsVsTargets(targets: Damageable[]): void {
    this.checkCollisions(this.playerBullets, targets);
  }

  /** Check enemy bullets against player targets */
  checkEnemyBulletsVsTargets(targets: Damageable[]): void {
    this.checkCollisions(this.enemyBullets, targets);
  }

  private checkCollisions(bullets: Bullet[], targets: Damageable[]): void {
    for (let bi = bullets.length - 1; bi >= 0; bi--) {
      const bullet = bullets[bi];
      if (!bullet.active) continue;
      if (bullet.distanceTravelled < BULLET_ARMING_DISTANCE) continue;

      for (const target of targets) {
        if (target.isDead()) continue;
        const dist = Phaser.Math.Distance.Between(bullet.x, bullet.y, target.x, target.y);
        if (dist <= COLLISION_RADIUS) {
          target.takeDamage(bullet.damage);
          // Emit blood splat event at impact position
          this.scene.events.emit('bloodSplat', bullet.x, bullet.y);
          bullet.destroy();
          bullets.splice(bi, 1);
          break;
        }
      }
    }
  }

  getPlayerBullets(): Bullet[] {
    return this.playerBullets;
  }

  getEnemyBullets(): Bullet[] {
    return this.enemyBullets;
  }

  destroyAll(): void {
    for (const bullet of [...this.playerBullets, ...this.enemyBullets]) {
      bullet.destroy();
    }
    this.playerBullets = [];
    this.enemyBullets = [];
  }
}
